import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, X, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TrialBanner = () => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [trialInfo, setTrialInfo] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchTrial = async () => {
      try {
        const res = await axios.get(`${API}/auth/trial-status`);
        setTrialInfo(res.data);
        if (res.data.is_trial && (res.data.trial_active || res.data.trial_expired)) {
          const lastShown = sessionStorage.getItem('trial_popup_shown');
          if (!lastShown) {
            setShowPopup(true);
            sessionStorage.setItem('trial_popup_shown', 'true');
          }
        }
      } catch {}
    };
    fetchTrial();
  }, []);

  if (!trialInfo || !trialInfo.is_trial || dismissed) return null;

  const isExpired = trialInfo.trial_expired;
  const hoursLeft = trialInfo.hours_left || 0;
  const daysLeft = trialInfo.days_left || 0;

  const timeText = daysLeft >= 1
    ? `${Math.floor(daysLeft)}d ${Math.round((daysLeft % 1) * 24)}h`
    : `${Math.round(hoursLeft)}h`;

  return (
    <>
      {/* Persistent Trial Strip */}
      <div className={`flex items-center justify-between px-4 py-2 text-sm ${
        isExpired
          ? 'bg-red-500/10 border-b border-red-500/20 text-red-400'
          : 'bg-amber-500/10 border-b border-amber-500/20 text-amber-500'
      }`}>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          {isExpired ? (
            <span>Trial expired. Subscribe to continue using all features.</span>
          ) : (
            <span>Trial: <strong>{timeText}</strong> remaining</span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => navigate('/subscription')}
          className={`h-7 text-xs ${isExpired ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
        >
          <Crown className="w-3 h-3 mr-1" /> Subscribe
        </Button>
      </div>

      {/* Login Popup */}
      <AnimatePresence>
        {showPopup && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={() => setShowPopup(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`relative z-10 w-[400px] max-w-[calc(100vw-32px)] rounded-2xl shadow-2xl border p-6 ${
                isDark ? 'bg-[#2E3944] border-white/10 text-white' : 'bg-white border-gray-200'
              }`}
            >
              <button
                onClick={() => setShowPopup(false)}
                className={`absolute top-4 right-4 p-1 rounded-lg ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X className="w-4 h-4" />
              </button>

              <div className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                  isExpired ? 'bg-red-500/10' : 'bg-amber-500/10'
                }`}>
                  {isExpired ? (
                    <X className="w-8 h-8 text-red-400" />
                  ) : (
                    <Clock className={`w-8 h-8 ${isExpired ? 'text-red-400' : 'text-amber-500'}`} />
                  )}
                </div>

                <h2 className="text-xl font-semibold mb-2">
                  {isExpired ? 'Trial Period Ended' : 'Free Trial Active'}
                </h2>

                {isExpired ? (
                  <p className={`text-sm mb-5 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Your free trial has ended. Subscribe now to continue accessing all features of PropertyShop including marketplace listings, AI assistant, and more.
                  </p>
                ) : (
                  <>
                    <div className={`text-4xl font-bold mb-1 ${isDark ? 'text-amber-400' : 'text-amber-500'}`}>
                      {timeText}
                    </div>
                    <p className={`text-sm mb-5 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      remaining in your free trial. Enjoy all features while it lasts! Subscribe anytime to keep access.
                    </p>
                  </>
                )}

                <div className="space-y-2">
                  <Button
                    onClick={() => { setShowPopup(false); navigate('/subscription'); }}
                    className="w-full btn-gold text-black font-semibold"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {isExpired ? 'Subscribe Now' : 'View Plans'}
                  </Button>
                  {!isExpired && (
                    <Button
                      variant="ghost"
                      onClick={() => setShowPopup(false)}
                      className={`w-full ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      Continue Trial
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
